import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { memberStatus as memberStatusSelector, isLoggedIn as isLoggedInSelector } from 'redux/saga/selectors/user';
import { selectUserCredits } from 'redux/saga/helpers/credit-cycle';
import { pathSelector } from 'redux/saga/selectors/request';
import { userProfile } from 'redux/saga/entities/bound';
import { selectIsPrelaunchMode } from 'redux/modules/page';
import { Header } from 'components/Header/Header';
import { HeaderUserDetails } from 'components/Header/HeaderUserDetails';
import { PortalDestination } from 'components/Portal/PortalDestination';
import media from 'decorators/Media/Media';
import { CP_LIVE_URL_REGEX } from 'constants/video';
import { CP_GO_URL_REGEX } from 'constants/go';
import { memberStatus as memberStatusEnum } from 'enums/memberStatus';
import { SEARCH_PATH } from 'features/Search/Search.route.path';
import { SearchBoxContainer } from 'features/Search/SearchBox/SearchBoxContainer';
import { decorateWith } from 'helpers/decorator';
import * as OptimizelyUtil from 'helpers/optimizely';
import { CLASS_DETAIL_ROUTE } from 'features/InventoryDetail/ClassDetail.route.path';
import { VENUE_DETAIL_ROUTE } from 'features/InventoryDetail/InventoryDetail.route.path';
import { NavigationCta } from './NavigationCta';
import { LoggedInUserLinksContainer } from './NavItems/LoggedInUserLinksContainer';
import { Navigation } from './Navigation';
import { CreditCount } from './NavItems/CreditCount';

const mapStateToProps = (state, { mediaQueryLg }) => {
  const memberStatus = memberStatusSelector(state);
  const path = pathSelector(state);
  const isPrelaunchMode = selectIsPrelaunchMode(state);
  const isLivePage = CP_LIVE_URL_REGEX.test(path);
  const isGoPage = CP_GO_URL_REGEX.test(path);
  const pagesWithSearchBar = [CLASS_DETAIL_ROUTE, SEARCH_PATH, VENUE_DETAIL_ROUTE];
  const shouldShowSearchOnHomepage = path === '/' && OptimizelyUtil.isActiveVariationFromConfig('searchOnHome');
  const hasSearchBar =
    shouldShowSearchOnHomepage || (mediaQueryLg && pagesWithSearchBar.some(pagePath => path.startsWith(pagePath)));

  return {
    creditsRemaining: selectUserCredits(state).credits_remaining,
    hasSearchBar,
    isDark: state.page.darkNav,
    isGoPage,
    isLivePage,
    isSearchPage: path.startsWith(SEARCH_PATH),
    isLoggedIn: isLoggedInSelector(state),
    isMinimal: state.page.reducedNav,
    isTransparentWhenAtScrollTop: false,
    memberStatus,
    path,
    isPrelaunchMode,
    showCreditCount: memberStatusEnum('ACTIVE', memberStatus),
    showCta: !state.page.sellHeader && !isLivePage,
    user: userProfile.data(state),
  };
};

export class AppHeader extends Component {
  state = { dropdownExpanded: false };

  componentDidUpdate(prevProps) {
    if (prevProps.path !== this.props.path) {
      this.handleNavigation();
    }
  }

  handleDropdownStateToggled = () => this.setState(state => ({ dropdownExpanded: !state.dropdownExpanded }));

  handleNavigation = () => this.setState({ dropdownExpanded: false });

  renderPrelaunchIcon() {
    return this.props.isPrelaunchMode ? (
      <div className="header__prelaunch">
        <span>prelaunch</span>
      </div>
    ) : null;
  }

  renderSearch() {
    return this.props.hasSearchBar ? (
      <div className="header__search">
        <SearchBoxContainer />
      </div>
    ) : null;
  }

  render() {
    const {
      isTransparentWhenAtScrollTop,
      creditsRemaining,
      showCreditCount,
      mediaQueryLg,
      memberStatus,
      hasSearchBar,
      isDark,
      isLoggedIn,
      isLivePage,
      isSearchPage,
      isGoPage,
      isMinimal,
      showCta,
      user,
    } = this.props;

    const { dropdownExpanded } = this.state;

    const navigationProps = {
      user,
      isLivePage,
      isGoPage,
      isActiveUser: memberStatusEnum('ACTIVE', memberStatus),
      isUnpaidUser: memberStatusEnum('UNPAID', memberStatus),
      isCancelledUser: memberStatusEnum('CANCELLED', memberStatus),
      isUnsubscribedUser: memberStatusEnum('UNSUBSCRIBED', memberStatus),
      isLoggedIn,
      isSearchPage,
      hasSearchBar,
    };

    return (
      <>
        <Header
          isDark={isDark}
          isTransparentWhenAtScrollTop={isTransparentWhenAtScrollTop}
          onDropdownStateToggled={this.handleDropdownStateToggled}
          dropdownOpen={dropdownExpanded}
          showHamburger={!memberStatus}
          isMobile={!mediaQueryLg}>
          {this.renderPrelaunchIcon()}

          {this.renderSearch()}

          <div className="header__spacer" />

          {!isMinimal && mediaQueryLg && <Navigation {...navigationProps} />}

          {!isMinimal && showCta && <NavigationCta {...navigationProps} />}

          <PortalDestination id="nav-cta" />

          {showCreditCount && <CreditCount creditsRemaining={creditsRemaining} />}

          {memberStatus && (
            <HeaderUserDetails
              firstName={user.first_name}
              mediaQueryLg={mediaQueryLg}
              dropdownOpen={dropdownExpanded}
              onDropdownStateToggled={this.handleDropdownStateToggled}>
              <ul>
                <LoggedInUserLinksContainer />
              </ul>
            </HeaderUserDetails>
          )}
        </Header>

        {dropdownExpanded && !mediaQueryLg && (
          <Navigation {...navigationProps} onClick={this.handleNavigation} isMobile />
        )}
      </>
    );
  }
}

AppHeader.propTypes = {
  canViewUnifiedSearch: PropTypes.bool,
  creditsRemaining: PropTypes.number,
  hasSearchBar: PropTypes.bool.isRequired,
  isDark: PropTypes.bool,
  isGoPage: PropTypes.bool,
  isLivePage: PropTypes.bool,
  isSearchPage: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  isMinimal: PropTypes.bool,
  isTransparentWhenAtScrollTop: PropTypes.bool,
  mediaQueryLg: PropTypes.bool,
  isClient: PropTypes.bool,
  memberStatus: PropTypes.string,
  path: PropTypes.string.isRequired,
  isPrelaunchMode: PropTypes.bool,
  showCreditCount: PropTypes.bool,
  showCta: PropTypes.bool,
  user: PropTypes.object,
};

export const AppHeaderContainer = decorateWith(connect(mapStateToProps), media)(AppHeader);
